import { AxiosInstance } from 'axios';
import AxiosFlexeServeInstance from '../shared/AxiosFlexeServe';
import {
  ConfirmCorrelationResponse,
  DevicesStoreInfoResponseV2,
  OpeningTimesWeekSetting,
  OpeningTimesStoreResponse,
  SetOpeningTimesResponse,
  StoreInfoResponse,
  CorrelationIDResponseAPI,
  TriggerDayPartAPIRequest,
} from './StoreService.model';
import confirmCorrelationIdRecursive from '../shared/confirmCorrelationIdRecursive';
import Environment from '../../shared/constants/Environment';

/**
 * @description This service is responsible for all the authentication related
 * operations.
 */
class StoreService {
  private readonly baseUrl = Environment.backendAPIFlexeServeOperator;

  public constructor(private axios: AxiosInstance = AxiosFlexeServeInstance) {}

  public async getStoreInfo(): Promise<StoreInfoResponse> {
    const response = await this.axios.get<StoreInfoResponse>(this.baseUrl, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });

    return response.data;
  }

  public async getOpeningTimesByStore(): Promise<OpeningTimesStoreResponse> {
    const url = `${this.baseUrl}/store/openingtimes`;
    const response = await this.axios.get<OpeningTimesStoreResponse>(url, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });

    return response.data;
  }

  public async setOpeningTimesByStore(
    openingTimes: OpeningTimesWeekSetting
  ): Promise<SetOpeningTimesResponse> {
    const url = `${this.baseUrl}/store/openingtimes`;
    const response = await this.axios.post<SetOpeningTimesResponse>(
      url,
      openingTimes
    );
    return response.data;
  }

  /** confirm call wit correlationId */
  public getStatusByCorrelationId = async (
    encodedDeviceID: string,
    correlationID: string
  ): Promise<ConfirmCorrelationResponse> => {
    const url = `${this.baseUrl}/${encodedDeviceID}/response/${correlationID}/v2`;
    const response = await this.axios.get<ConfirmCorrelationResponse>(url);
    return response.data;
  };

  /**
   * confirm call with correlationId, with exponential backOff
   * throw error if passed more seconds than `SECONDS_TIMEOUT`
   * throw error if passed more tries than `NUM_TRIES`
   * throw error if not found correlationId
   * throw error if not case founded
   */
  public async confirmCorrelationIdRecursive(
    encodedDeviceID: string,
    correlationID: string,
    getStatusByCorrelationIdPromise = this.getStatusByCorrelationId
  ): Promise<any> {
    return confirmCorrelationIdRecursive(
      encodedDeviceID,
      correlationID,
      getStatusByCorrelationIdPromise
    );
  }

  public async getStoreInfoDetails(): Promise<DevicesStoreInfoResponseV2> {
    const url = `${this.baseUrl}/store/v2`;
    const response = await this.axios.get<DevicesStoreInfoResponseV2>(url, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });

    return response.data;
  }

  public async triggerDayPart(
    encodedDeviceID: string,
    data: TriggerDayPartAPIRequest
  ): Promise<boolean> {
    const url = `${this.baseUrl}/${encodedDeviceID}/triggerdaypart`;
    const response = await this.axios.post<CorrelationIDResponseAPI>(url, data);
    return await this.confirmCorrelationIdRecursive(
      encodedDeviceID,
      response.data.correlationID
    );
  }

  public async setScheduleRequireUpdate(
    encodedDeviceID: string
  ): Promise<boolean> {
    const url = `${this.baseUrl}/${encodedDeviceID}/schedule`;
    const response = await this.axios.post<CorrelationIDResponseAPI>(url);
    return await this.confirmCorrelationIdRecursive(
      encodedDeviceID,
      response.data.correlationID
    );
  }
}

export default new StoreService();
